import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";
import Logo from "./Logo";

const Header = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { type: "scroll", id: "about", label: t.nav.about },
    { type: "scroll", id: "audience", label: t.nav.services },
    { type: "route", to: "/oferty-pracy", label: t.nav.jobs },
    { type: "route", to: "/faq", label: t.nav.faq },
    { type: "scroll", id: "contact", label: t.nav.contact },
  ];

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handle = (item) => {
    setMobileOpen(false);
    if (item.type === "route") {
      navigate(item.to);
      window.scrollTo(0, 0);
    } else {
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => scrollToId(item.id), 280);
      } else {
        scrollToId(item.id);
      }
    }
  };

  const goHome = () => {
    if (location.pathname !== "/") navigate("/");
    window.scrollTo({ top: 0, behavior: "smooth" });
    setMobileOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="container-px mx-auto max-w-7xl flex items-center justify-between py-4">
        <button onClick={goHome} className="shrink-0">
          <Logo />
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.label}
              onClick={() => handle(l)}
              className="relative text-sm font-medium tracking-wide text-white/75 hover:text-white transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-white after:transition-all hover:after:w-full"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <button
            onClick={() => handle({ type: "scroll", id: "contact" })}
            className="hidden md:inline-flex glass-btn-solid glass-btn px-5 py-2 text-sm font-semibold"
          >
            {t.nav.cta}
          </button>
          <button
            className="lg:hidden glass-btn p-2"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-black/90 backdrop-blur-xl border-t border-white/10 animate-fade-up">
          <nav className="container-px mx-auto max-w-7xl flex flex-col py-4">
            {links.map((l) => (
              <button
                key={l.label}
                onClick={() => handle(l)}
                className="py-3 text-left text-base font-medium text-white/80 hover:text-white border-b border-white/5"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => handle({ type: "scroll", id: "contact" })}
              className="glass-btn-solid glass-btn mt-4 py-3 text-sm font-semibold"
            >
              {t.nav.cta}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
